'use client';

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileText, Code, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { previewImport } from '@/services/article';
import { useRouter } from 'next/navigation';

interface ArticleImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ArticleImportDialog({ open, onOpenChange }: ArticleImportDialogProps) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [importType, setImportType] = useState<'html' | 'word'>('html');
  const [htmlContent, setHtmlContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setHtmlContent('');
    setSelectedFile(null);
    setError(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const validExtension = importType === 'html'
        ? ['html', 'htm'].includes(extension || '')
        : ['docx'].includes(extension || '');

      if (!validExtension) {
        setError(importType === 'html'
          ? 'HTMLファイル(.html, .htm)を選択してください。'
          : 'Wordファイル(.docx)を選択してください。');
        return;
      }

      setSelectedFile(file);
      setError(null);

      // HTMLファイルの場合は内容を読み込む
      if (importType === 'html') {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          setHtmlContent(content);
        };
        reader.readAsText(file);
      }
    }
  };

  // ファイルをパースしてプレビューページへ遷移
  const handlePreview = async () => {
    setError(null);
    setIsLoading(true);

    try {
      if (importType === 'html' && !htmlContent.trim()) {
        setError('HTMLコンテンツを入力またはファイルを選択してください。');
        setIsLoading(false);
        return;
      }

      if (importType === 'word' && !selectedFile) {
        setError('Wordファイルを選択してください。');
        setIsLoading(false);
        return;
      }

      const response = await previewImport({
        import_type: importType,
        html_content: importType === 'html' ? htmlContent : undefined,
        file: importType === 'word' ? selectedFile! : undefined,
      });

      if (response.success && response.data) {
        // sessionStorageにデータを保存してプレビューページへ遷移
        const previewData = {
          ...response.data,
          import_source: importType,
        };
        sessionStorage.setItem('importPreviewData', JSON.stringify(previewData));

        // ダイアログを閉じてプレビューページへ
        onOpenChange(false);
        resetForm();
        router.push('/article/import');
      } else {
        setError(response.message || 'パースに失敗しました。');
      }
    } catch (err) {
      setError('パース中にエラーが発生しました。');
      console.error('Preview error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            記事をインポート
          </DialogTitle>
          <DialogDescription>
            HTMLまたはWordファイルをアップロードして、既存の記事をインポートします。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Tabs value={importType} onValueChange={(v) => {
            setImportType(v as 'html' | 'word');
            setSelectedFile(null);
            setHtmlContent('');
            setError(null);
            // ファイル入力をリセット
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="html" className="flex items-center gap-2">
                <Code className="h-4 w-4" />
                HTML
              </TabsTrigger>
              <TabsTrigger value="word" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Word
              </TabsTrigger>
            </TabsList>

            <TabsContent value="html" className="space-y-4">
              <div className="space-y-2">
                <Label>HTMLコンテンツ</Label>
                <Textarea
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="HTMLコードを貼り付けてください..."
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <div className="text-center text-sm text-muted-foreground">または</div>
              <div className="space-y-2">
                <Label>HTMLファイルをアップロード</Label>
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {selectedFile ? selectedFile.name : 'クリックしてファイルを選択'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">.html, .htm</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".html,.htm"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </TabsContent>

            <TabsContent value="word" className="space-y-4">
              <div className="space-y-2">
                <Label>Wordファイルをアップロード</Label>
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {selectedFile ? selectedFile.name : 'クリックしてファイルを選択'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">.docx のみ対応</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* エラー表示 */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* アクションボタン */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
              キャンセル
            </Button>
            <Button onClick={handlePreview} disabled={isLoading} className="flex-1">
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  読み込み中...
                </>
              ) : (
                <>
                  次へ：プレビュー
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
